import React from 'react'
import type { MarketplaceJob, MarketplaceWorkflowStatus } from '../../../services/backendService'
import { StarRating } from './StarRating'
import { loadQualityPhotos, readFileAsDataUrl, saveQualityPhotos, type QualityPhoto, type QualityPhotosRecord } from '../utils/qualityPhotos'
import '../styles/FarmActiveJobsList.css'

interface FarmActiveJobsListProps {
  jobs: MarketplaceJob[]
  selectedJob: MarketplaceJob | null
  updatingJobId?: string | null
  onSelectJob: (job: MarketplaceJob) => void
  onBack: () => void
  onAdvanceStatus: (jobId: string, nextStatus: MarketplaceWorkflowStatus) => void | Promise<void>
}

const NEXT_STATUS_BY_STAGE: Record<string, MarketplaceWorkflowStatus | undefined> = {
  in_progress: 'printing',
  printing: 'quality_check',
  quality_check: 'shipped',
  shipped: undefined,
  delivered: 'completed'
}

interface LocalMarketplaceReview {
  jobId: string
  reviewerSide: 'doctor' | 'farm'
  targetSide: 'farm' | 'doctor'
  targetName: string
  rating: number
  comment: string
  createdAt: string
}

const formatStatus = (status?: string | null) => (status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, match => match.toUpperCase())
const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—')

export const FarmActiveJobsList: React.FC<FarmActiveJobsListProps> = ({
  jobs,
  selectedJob,
  updatingJobId,
  onSelectJob,
  onBack,
  onAdvanceStatus
}) => {
  const [viewMode, setViewMode] = React.useState<'card' | 'list'>('card')
  const [reviewRating, setReviewRating] = React.useState(0)
  const [reviewComment, setReviewComment] = React.useState('')
  const [reviewError, setReviewError] = React.useState<string | null>(null)
  const [reviewSavedAt, setReviewSavedAt] = React.useState<string | null>(null)
  const [qualityRecord, setQualityRecord] = React.useState<QualityPhotosRecord>({ photos: [], approved: false, approvedAt: null, rejectedAt: null })
  const [qualityPhotosError, setQualityPhotosError] = React.useState<string | null>(null)
  const [stagedPhotos, setStagedPhotos] = React.useState<QualityPhoto[]>([])
  const [isSendingPhotos, setIsSendingPhotos] = React.useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false)

  React.useEffect(() => {
    if (!selectedJob) {
      setReviewRating(0)
      setReviewComment('')
      setReviewSavedAt(null)
      return
    }

    const storageKey = 'marketplace-reviews'
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) {
      setReviewSavedAt(null)
      return
    }

    try {
      const parsed = JSON.parse(raw) as LocalMarketplaceReview[]
      const saved = parsed.find(review => review.jobId === selectedJob.id && review.reviewerSide === 'farm')
      if (!saved) {
        setReviewSavedAt(null)
        return
      }

      setReviewRating(saved.rating)
      setReviewComment(saved.comment)
      setReviewSavedAt(saved.createdAt)
    } catch {
      setReviewSavedAt(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id])

  React.useEffect(() => {
    if (!selectedJob) {
      setQualityRecord({ photos: [], approved: false, approvedAt: null, rejectedAt: null })
      setQualityPhotosError(null)
      setStagedPhotos([])
      return
    }
    setQualityRecord(loadQualityPhotos(selectedJob.id))
    setQualityPhotosError(null)
    setStagedPhotos([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id])

  const handleQualityPhotosChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setQualityPhotosError(null)

    if (!selectedJob || files.length === 0) {
      event.target.value = ''
      return
    }

    const savedCount = qualityRecord.photos.length
    const remainingSlots = Math.max(0, 3 - savedCount - stagedPhotos.length)
    if (remainingSlots === 0) {
      setQualityPhotosError('You already staged or saved the maximum of 3 photos. Remove one before adding another.')
      event.target.value = ''
      return
    }

    const accepted = files.slice(0, remainingSlots)

    try {
      const photos = await Promise.all(accepted.map(async file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        dataUrl: await readFileAsDataUrl(file)
      })))
      setStagedPhotos(prev => [...prev, ...photos])
    } catch {
      setQualityPhotosError('Unable to read one of the selected images. Please retry.')
    } finally {
      event.target.value = ''
    }
  }

  const handleRemoveStagedPhoto = (index: number) => {
    setStagedPhotos(prev => prev.filter((_, idx) => idx !== index))
  }

  const handleSendPhotos = () => {
    if (!selectedJob || stagedPhotos.length === 0) return
    setIsSendingPhotos(true)
    try {
      const combined = [...qualityRecord.photos, ...stagedPhotos].slice(0, 3)
      const next: QualityPhotosRecord = {
        photos: combined,
        approved: false,
        approvedAt: null,
        rejectedAt: null
      }
      saveQualityPhotos(selectedJob.id, next)
      setQualityRecord(next)
      setStagedPhotos([])
      setQualityPhotosError(null)
    } catch {
      setQualityPhotosError('Unable to send photos right now. Please try again.')
    } finally {
      setIsSendingPhotos(false)
    }
  }

  const refreshQualityRecord = () => {
    if (!selectedJob) return
    setQualityRecord(loadQualityPhotos(selectedJob.id))
  }

  const handleAdvanceStatus = async (jobId: string, nextStatus: MarketplaceWorkflowStatus, currentStatus: string) => {
    if (currentStatus === 'quality_check' && nextStatus === 'shipped') {
      if (qualityRecord.photos.length < 3) {
        setQualityPhotosError('Upload 3 final product photos before marking this job as shipped.')
        return
      }
      if (!qualityRecord.approved) {
        setQualityPhotosError('Waiting for the doctor to approve the quality check photos before shipment.')
        return
      }
    }

    if (currentStatus === 'delivered' && nextStatus === 'completed') {
      if (reviewRating <= 0 || reviewComment.trim().length === 0 || !reviewSavedAt) {
        setReviewError('Save a doctor rating and comment before completing this job.')
        return
      }
    }

    setQualityPhotosError(null)
    await onAdvanceStatus(jobId, nextStatus)
  }

  const handleSaveDoctorReview = () => {
    if (!selectedJob) {
      return
    }

    if (reviewRating <= 0) {
      setReviewError('Pick a star rating before saving your review.')
      return
    }

    const comment = reviewComment.trim()
    if (!comment) {
      setReviewError('Add a comment before saving your review.')
      return
    }

    setReviewError(null)
    const storageKey = 'marketplace-reviews'
    let reviews: LocalMarketplaceReview[] = []
    const existing = window.localStorage.getItem(storageKey)

    if (existing) {
      try {
        const parsed = JSON.parse(existing) as unknown
        reviews = Array.isArray(parsed) ? parsed as LocalMarketplaceReview[] : []
      } catch {
        // Recover from malformed storage so Save Review still works.
        reviews = []
      }
    }

    const nextReview: LocalMarketplaceReview = {
      jobId: selectedJob.id,
      reviewerSide: 'farm',
      targetSide: 'doctor',
      targetName: selectedJob.doctorName,
      rating: reviewRating,
      comment,
      createdAt: new Date().toISOString()
    }

    const withoutCurrent = reviews.filter(review => !(review.jobId === selectedJob.id && review.reviewerSide === 'farm'))
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([nextReview, ...withoutCurrent]))
      setReviewSavedAt(nextReview.createdAt)
    } catch {
      setReviewError('Unable to save review right now. Please try again.')
    }
  }

  if (selectedJob) {
    const nextStatus = NEXT_STATUS_BY_STAGE[selectedJob.status]
    const canReviewDoctor = selectedJob.status === 'delivered' || selectedJob.status === 'completed'
    const canCancel = ['in_progress', 'printing', 'quality_check'].includes(selectedJob.status)

    return (
      <div className="farm-active-jobs">
        <button className="btn-back" onClick={onBack}>← Back to Active Jobs</button>
        <div className="farm-active-job-details">
          <div className="farm-active-job-header">
            <div>
              <h3>{selectedJob.title}</h3>
              <p>{selectedJob.description}</p>
            </div>
            <span className={`farm-active-job-status status-${selectedJob.status}`}>{formatStatus(selectedJob.status)}</span>
          </div>
          <div className="farm-active-job-grid">
            <div><strong>Doctor:</strong> {selectedJob.doctorName}</div>
            <div><strong>Material:</strong> {selectedJob.material}</div>
            <div><strong>Deadline:</strong> {selectedJob.deadline}</div>
            <div><strong>Selected via:</strong> {formatStatus(selectedJob.selectionType)}</div>
            <div><strong>STL file:</strong> {selectedJob.stlFileName}</div>
          </div>
          {selectedJob.status === 'quality_check' && (
            <div className="farm-quality-check-panel">
              <h4>Quality Check Photos</h4>
              <p>Stage 3 final product photos, remove any you don&apos;t want, then press Send. The doctor must approve them before you can mark the job as shipped.</p>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => { void handleQualityPhotosChange(event) }}
              />
              <p>{qualityRecord.photos.length}/3 photos sent{stagedPhotos.length > 0 && ` (+${stagedPhotos.length} staged)`}</p>
              {(qualityRecord.photos.length > 0 || stagedPhotos.length > 0) && (
                <div className="quality-photo-gallery" aria-label="Uploaded quality check photos">
                  {qualityRecord.photos.map((photo, index) => (
                    photo.dataUrl ? (
                      <img
                        key={`saved-${photo.name}-${index}`}
                        src={photo.dataUrl}
                        alt={`Quality check photo ${index + 1}`}
                        className="quality-photo-thumb"
                      />
                    ) : (
                      <div key={`saved-${photo.name}-${index}`} className="quality-photo-thumb is-placeholder">{photo.name}</div>
                    )
                  ))}
                  {stagedPhotos.map((photo, index) => (
                    <div key={`staged-${photo.name}-${index}`} className="quality-photo-thumb-wrap is-staged">
                      {photo.dataUrl ? (
                        <img
                          src={photo.dataUrl}
                          alt={`Staged photo ${index + 1}`}
                          className="quality-photo-thumb"
                        />
                      ) : (
                        <div className="quality-photo-thumb is-placeholder">{photo.name}</div>
                      )}
                      <button
                        type="button"
                        className="quality-photo-remove"
                        aria-label={`Remove staged photo ${index + 1}`}
                        onClick={() => handleRemoveStagedPhoto(index)}
                      >×</button>
                      <span className="quality-photo-staged-label">Staged</span>
                    </div>
                  ))}
                </div>
              )}
              {stagedPhotos.length > 0 && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSendPhotos}
                  disabled={isSendingPhotos}
                  style={{ marginTop: '8px' }}
                >
                  {isSendingPhotos ? 'Sending…' : `Send ${stagedPhotos.length} photo${stagedPhotos.length === 1 ? '' : 's'} to doctor`}
                </button>
              )}
              {qualityRecord.photos.length >= 3 && !qualityRecord.approved && (
                <p className="farm-quality-check-pending">
                  Awaiting doctor approval.
                  <button type="button" className="btn-secondary" onClick={refreshQualityRecord} style={{ marginLeft: '8px' }}>Refresh</button>
                </p>
              )}
              {qualityRecord.approved && (
                <p className="farm-quality-check-approved">Doctor approved on {qualityRecord.approvedAt ? new Date(qualityRecord.approvedAt).toLocaleString() : '—'}.</p>
              )}
              {qualityRecord.rejectedAt && !qualityRecord.approved && qualityRecord.photos.length === 0 && (
                <p className="farm-quality-check-rejected">Doctor rejected the previous photos. Please re-upload 3 new photos.</p>
              )}
              {qualityPhotosError && <p className="farm-quality-check-error">{qualityPhotosError}</p>}
            </div>
          )}
          <div className="farm-active-job-timeline">
            <h4>Workflow timeline</h4>
            <div><strong>Current Stage</strong><span>{formatStatus(selectedJob.workflow?.currentStage || selectedJob.status)}</span></div>
            <div><strong>Accepted At</strong><span>{formatDateTime(selectedJob.workflow?.timestamps?.acceptedAt)}</span></div>
            <div><strong>Last Updated</strong><span>{formatDateTime(selectedJob.workflow?.timestamps?.updatedAt || selectedJob.updatedAt)}</span></div>
            <div><strong>Completed At</strong><span>{formatDateTime(selectedJob.workflow?.timestamps?.closedAt || selectedJob.closedAt)}</span></div>
          </div>
          {nextStatus && (
            <div className="farm-active-job-actions">
              <button
                className="btn-primary"
                onClick={() => void handleAdvanceStatus(selectedJob.id, nextStatus, selectedJob.status)}
                disabled={updatingJobId === selectedJob.id}
              >
                {updatingJobId === selectedJob.id ? 'Updating…' : `Mark as ${formatStatus(nextStatus)}`}
              </button>
            </div>
          )}
          {canCancel && (
            <div className="farm-active-job-actions">
              {!showCancelConfirm ? (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={updatingJobId === selectedJob.id}
                  data-testid="cancel-job-btn"
                >
                  Cancel Job
                </button>
              ) : (
                <div className="farm-cancel-confirm" data-testid="cancel-job-confirm">
                  <p>Are you sure you want to cancel this job? The job will be returned to <strong>Open</strong> so the doctor can find a new farm.</p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => { void onAdvanceStatus(selectedJob.id, 'cancelled'); setShowCancelConfirm(false) }}
                      disabled={updatingJobId === selectedJob.id}
                      data-testid="cancel-job-confirm-btn"
                    >
                      {updatingJobId === selectedJob.id ? 'Cancelling…' : 'Yes, cancel job'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowCancelConfirm(false)}
                      disabled={updatingJobId === selectedJob.id}
                    >
                      Keep job
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedJob.status === 'shipped' && (
            <div className="farm-active-job-actions">
              <p>Waiting for doctor delivery confirmation before moving this job to delivered.</p>
            </div>
          )}

          {canReviewDoctor && (
            <div className="farm-active-job-actions">
              <h4>Review Doctor</h4>
              <div style={{ marginBottom: '8px' }}>
                <StarRating value={reviewRating} onChange={setReviewRating} ariaLabel="Doctor rating" />
              </div>
              <textarea
                value={reviewComment}
                onChange={(event) => setReviewComment(event.target.value)}
                placeholder="Write your review comment"
                rows={3}
              />
              {reviewError && <p style={{ color: '#b91c1c' }}>{reviewError}</p>}
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveDoctorReview}
                disabled={reviewRating <= 0 || reviewComment.trim().length === 0}
              >
                Save Review
              </button>
              {reviewSavedAt && <p>Saved on {new Date(reviewSavedAt).toLocaleString()}</p>}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="farm-active-jobs">
      <div className="farm-active-jobs-list">
        <div className="farm-active-jobs-header">
          <h3>Active Jobs ({jobs.length})</h3>
          <div className="farm-active-jobs-view-toggle" aria-label="Active jobs view mode">
            <button
              type="button"
              className={`farm-active-jobs-view-toggle-btn ${viewMode === 'card' ? 'is-active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`farm-active-jobs-view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>
        {jobs.length === 0 ? (
          <div className="empty-state">
            <p>No active jobs assigned yet. Accepted marketplace work will appear here.</p>
          </div>
        ) : (
          <div className={`farm-active-jobs-grid ${viewMode !== 'card' ? 'list-view' : ''}`}>
            {jobs.map(job => (
              <button
                key={job.id}
                type="button"
                className={`farm-active-job-card ${viewMode !== 'card' ? 'list-view compact-view' : ''}`.trim()}
                onClick={() => onSelectJob(job)}
              >
                <div className="farm-active-job-card-header">
                  <h4>{job.title}</h4>
                  <span className={`farm-active-job-status status-${job.status}`}>{formatStatus(job.status)}</span>
                </div>
                <p>{job.description}</p>
                <div className="farm-active-job-meta">
                  <span><strong>Doctor:</strong> {job.doctorName}</span>
                  <span><strong>Deadline:</strong> {job.deadline}</span>
                  <span><strong>Updated:</strong> {formatDateTime(job.updatedAt || job.workflow?.timestamps?.updatedAt)}</span>
                </div>
                <span className="farm-active-job-link">View workflow for {job.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}